import React, { useState } from 'react';
import {
  agePensionAssetFaqAnswer,
  agePensionDeemingFaqAnswer,
  agePensionMaxFaqAnswer,
  rentAssistanceFaqAnswer,
  renterRetirementFaqAnswer,
} from '../utils/statutoryHelpCopy.js';

const HelpCenter = () => {
  const [openSections, setOpenSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const faqData = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
      questions: [
        {
          q: 'How do I use the Basic Retirement Calculator?',
          a: 'Enter your current age, super balance, desired retirement income, and other details. The calculator will show you how long your super will last and suggest the maximum sustainable income.'
        },
        {
          q: 'What is the difference between Basic, Advanced, and Accumulation calculators?',
          a: 'Basic: Simple retirement planning with age pension. Advanced: Complex scenarios with Monte Carlo analysis and sequence risk. Accumulation: Project super growth during working years.'
        },
        {
          q: 'Do I need to create an account?',
          a: 'No account required for Free Calculators. Your data is saved locally in your browser. Advanced Retirement and All Access features require a subscription.'
        }
      ]
    },
    {
      id: 'calculations',
      title: 'Calculations & Accuracy',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      questions: [
        {
          q: 'How accurate are the calculations?',
          a: 'Our calculations use current ATO and Centrelink rules, updated regularly. We include age pension calculations, tax implications, and realistic fee structures. However, future policy changes may affect accuracy.'
        },
        {
          q: 'What assumptions does the calculator make?',
          a: 'We assume constant returns (you can adjust), current tax rates, age pension rules, and standard super fees. We don\'t account for market volatility, sequence risk, or future policy changes.'
        },
        {
          q: 'Why do my results differ from other calculators?',
          a: 'Different calculators use different assumptions about returns, fees, age pension calculations, and withdrawal strategies. Our calculator uses more detailed age pension modeling and realistic fee structures.'
        },
        {
          q: 'How does the age pension calculation work?',
          a: 'We use current Centrelink rules including asset and income tests, homeownership status, and relationship status. The pension amount adjusts based on your assets and other income.'
        }
      ]
    },
    {
      id: 'retirement-spending',
      title: 'Understanding Retirement Spending',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
      questions: [
        {
          q: 'How much will I really need in retirement?',
          a: 'Research shows 75% of retirees expect spending to stay the same or decrease. Rather than using arbitrary rules like "70% of your income", start with your actual current spending, remove work-related expenses (commuting, work clothes), and add retirement costs (travel, healthcare). Most retirees find they need less than they earn while working.'
        },
        {
          q: 'Why do some calculators give me such different retirement income estimates?',
          a: 'Many calculators use arbitrary assumptions like "replacement rates" or generic "comfortable retirement" standards. Research from Super Consumers Australia found retirement income estimates varied by 74% ($29,928 to $52,000) for the same person across different calculators. We use evidence-based spending patterns and your actual situation.'
        },
        {
          q: 'What spending categories should I consider?',
          a: 'Break spending into: Essential (housing, utilities, groceries, healthcare, insurance), Discretionary (travel, dining, entertainment, hobbies), One-off (car replacement, home renovations, appliances), and Contingency (emergency fund). Essential expenses typically stay steady while discretionary spending tends to decrease as you age.'
        },
        {
          q: 'Does spending really decrease in retirement?',
          a: 'Yes - analysis shows retirees have flat or falling spending relative to prices as they age. Active early retirement years (67-75) cost more for travel and activities, while later years (75+) see reduced discretionary spending but potentially higher healthcare costs. Plan for this natural decline rather than assuming constant high spending.'
        }
      ]
    },
    {
      id: 'housing',
      title: 'Homeowner vs Renter',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>,
      questions: [
        {
          q: 'How does being a renter affect my retirement income needs?',
          a: renterRetirementFaqAnswer()
        },
        {
          q: 'What is Commonwealth Rent Assistance and am I eligible?',
          a: rentAssistanceFaqAnswer()
        },
        {
          q: 'Should I try to pay off my mortgage before retirement?',
          a: 'Yes, paying off your mortgage before retirement is highly recommended. It eliminates a major expense, frees up cash flow, and converts a liability into an exempt asset for Age Pension purposes. Even small extra repayments can save years of interest. Consider using an offset account for your emergency fund to reduce interest while maintaining access to savings.'
        },
        {
          q: 'What if I have a mortgage at retirement?',
          a: 'If you still have a mortgage at retirement, prioritize paying it off quickly. Consider: (1) Making larger repayments while still working, (2) Using part of your super as a lump sum to clear the debt, (3) Downsizing to a smaller home to unlock equity, or (4) Relocating to an area with lower property prices. Our calculator can model these scenarios to show the impact on your retirement income.'
        }
      ]
    },
    {
      id: 'age-pension',
      title: 'Age Pension Explained',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
      questions: [
        {
          q: 'How much Age Pension can I get?',
          a: agePensionMaxFaqAnswer()
        },
        {
          q: 'What are the asset test thresholds?',
          a: agePensionAssetFaqAnswer()
        },
        {
          q: 'How do deeming rules work?',
          a: agePensionDeemingFaqAnswer()
        },
        {
          q: 'Can I get Age Pension if I have a lot of super?',
          a: 'Yes! Homeowner couples with up to $800,000 in super can still receive a part-pension. The pension tapers gradually, so even those above the full pension threshold often qualify for something. The income test uses deeming rules and the asset test excludes your home, making many retirees eligible for at least a partial pension. Always include potential pension in your planning.'
        },
        {
          q: 'What\'s the difference between asset test and income test?',
          a: 'Asset test: Based on total assets except your home. Income test: Based on deemed income from financial assets plus other income. Centrelink calculates both and you receive the LOWER amount. For retirees with high balances but modest actual income, the asset test usually applies. Those with significant rental or investment income may hit the income test first.'
        }
      ]
    },
    {
      id: 'withdrawal-rules',
      title: 'Super Withdrawal Rules',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      questions: [
        {
          q: 'When can I access my super?',
          a: 'You can access super at your preservation age (55-60 depending on birth year) if retired, or at age 60+ with a Transition to Retirement (TTR) pension if still working. Full unrestricted access comes when you retire after reaching preservation age. Age 60+ withdrawals are completely tax-free.'
        },
        {
          q: 'What are minimum withdrawal requirements?',
          a: 'Once in retirement phase, you must withdraw a minimum percentage each year: Under 65 (4%), 65-74 (5%), 75-79 (6%), 80-84 (7%), 85-89 (9%), 90-94 (11%), 95+ (14%). These are minimums - you can take more. The percentage is based on your balance at start of financial year. Many calculators ignore these rules, but ours enforces them correctly.'
        },
        {
          q: 'What is preservation age?',
          a: 'Preservation age is when you can first access super (if retired): Born before 1960 (age 55), 1960-61 (56), 1961-62 (57), 1962-63 (58), 1963-64 (59), 1964+ (age 60). This is different from Age Pension eligibility (67) and different from when withdrawals become tax-free (60). You can start a TTR pension at preservation age while still working.'
        },
        {
          q: 'Are super withdrawals taxed?',
          a: 'Age 60+: Completely tax-free for both lump sums and income streams. Under 60: Tax-free component is always tax-free. Taxed component is taxed at your marginal rate less 15% offset (lifetime cap of $230,000 for lump sums). Once in pension phase, investment earnings are also tax-free (0% vs 15% in accumulation phase). This is a huge tax advantage.'
        },
        {
          q: 'What\'s better - lump sum or income stream?',
          a: 'Income stream (pension) is generally better: (1) Investment earnings are tax-free (0% vs 15%), (2) Easier to manage regular income, (3) Can take ad-hoc lump sums when needed, (4) Maintains super\'s tax-free environment. Lump sum makes sense only for paying off debts, making major purchases, or if you have specific investment plans outside super. Most people use income stream with occasional lump sums as needed.'
        }
      ]
    },
    {
      id: 'withdrawal-strategies',
      title: 'Withdrawal Strategies',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
      questions: [
        {
          q: 'What are the different withdrawal strategies?',
          a: 'Fixed: Same amount each year adjusted for inflation - predictable but inflexible. Vanguard Guardrails: Adjusts withdrawals based on portfolio performance within bounds - balances income stability with sustainability. Dynamic SWR: Recalculates each year based on remaining balance and life expectancy - won\'t run out but income varies. Floor & Ceiling: Dynamic withdrawal within min/max limits - combines security with flexibility.'
        },
        {
          q: 'Which withdrawal strategy is best for me?',
          a: 'Large balance relative to needs: Fixed withdrawal works well and is simple. Tight budget with essential expenses: Floor & Ceiling provides security. Flexible discretionary expenses: Guardrails or Dynamic SWR let you adapt. Age Pension eligible: Fixed withdrawal works as pension provides income floor. Long retirement horizon (30+ years): Dynamic strategies reduce risk of depletion.'
        },
        {
          q: 'What is the "4% rule" and does it apply in Australia?',
          a: 'The 4% rule (withdraw 4% of initial balance, adjust for inflation) comes from US research. In Australia, it needs modification for: (1) Age Pension integration (provides safety net), (2) Compulsory minimum withdrawals (may force more than 4%), (3) Different market conditions, (4) Franking credits on Australian shares. Our calculator accounts for these Australian-specific factors.'
        },
        {
          q: 'Should I take only the minimum required withdrawal?',
          a: 'Not necessarily. While minimum withdrawals help preserve capital, consider: (1) Age Pension may reduce or stop if assets too high, (2) You can\'t spend it when you\'re gone, (3) Early retirement years are most active (travel, activities), (4) Health or mobility may limit later spending. Balance preservation with enjoying retirement. Many retirees die with significant unspent balances - don\'t be too conservative.'
        }
      ]
    },
    {
      id: 'longevity',
      title: 'Longevity & Planning Horizon',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      questions: [
        {
          q: 'How long should I plan for retirement to last?',
          a: 'Average life expectancy at 67: Males to 86, Females to 89. But there\'s a 50% chance you\'ll live longer. Conservative planning suggests age 95-100. For couples, there\'s a very high probability at least one partner lives past 90. Better to plan conservatively and have surplus than run short. Our calculator lets you model different scenarios to see the impact.'
        },
        {
          q: 'What is "sequence of returns risk"?',
          a: 'Sequence risk is when market crashes early in retirement devastate long-term outcomes, even if average returns are good. Example: If markets crash 30% in year 1-2 of retirement while you\'re withdrawing, you lock in losses and never recover. The same crash 15 years later has far less impact. This is why we test your plan against every historical period including hard starts such as 1969, 1973, and the 2008 GFC.'
        },
        {
          q: 'Should I plan for my money to run out at a certain age?',
          a: 'Set your "target final balance": $0 (maximize spending, rely on Age Pension at end), $50k-$100k (contingency buffer for unexpected costs), or higher (bequest/inheritance goal). With Age Pension as safety net, a $0 target can be reasonable for homeowners. Non-homeowners may want a larger buffer. Consider your home equity as a final safety net via downsizing if needed.'
        },
        {
          q: 'What about healthcare costs in later retirement?',
          a: 'Healthcare costs increase significantly with age. Budget more for years 80+. Consider: Private health insurance (keep to 70-75?), Aged care accommodation (RADs $300k-$500k+), Home care packages ($10k-$50k/year), Medical procedures and specialists. Medicare covers much but not everything. Home equity or super preservation can fund aged care if needed.'
        }
      ]
    },
    {
      id: 'common-mistakes',
      title: 'Common Planning Mistakes',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
      questions: [
        {
          q: 'What is the biggest mistake retirees make?',
          a: 'Using arbitrary "replacement rates" (70-80% of income) instead of calculating actual spending needs. Research shows retirees consistently overestimate how much they\'ll spend. Calculate from your actual expenses: remove work costs (commuting, clothes, lunches), add retirement costs (travel, healthcare), and you\'ll likely find you need less than you earn now. Your spending, not your income, determines your needs.'
        },
        {
          q: 'What mistakes do most retirement calculators make?',
          a: 'Super Consumers Australia found most calculators: (1) Use unreasonable assumptions (results varied 74%), (2) Don\'t help calculate actual spending needs, (3) Ignore rent or mortgage situations, (4) Don\'t explain minimum withdrawals, (5) Use generic "comfortable" standards not tailored to you. SuperCalc Pro was built specifically to address these industry-wide failures.'
        },
        {
          q: 'Do people really underestimate longevity?',
          a: 'Yes! Many plan to 80-85 when there\'s a 50% chance they\'ll live past 86-89. For couples, probability of at least one living past 90 is very high. Medical advances continue extending lifespans. Planning to 95-100 isn\'t pessimistic - it\'s realistic. Better to have surplus than run out. Age Pension provides a safety net, but quality of life is better with adequate savings.'
        },
        {
          q: 'Should I ignore Age Pension in my planning?',
          a: 'No! This is a common mistake. Even with significant super, many retirees qualify for part-pension. Homeowner couples with $800k super can still get part-pension. The pension tapers gradually, and deeming rules can work in your favor. Ignoring potential pension leads to overly conservative plans and unnecessary frugality. Always include realistic pension estimates.'
        },
        {
          q: 'Is it a mistake to be too conservative?',
          a: 'Yes! Many retirees die with large unspent balances, having sacrificed travel and experiences unnecessarily. Early retirement years (67-75) are your most active - don\'t defer everything to later when health or mobility may limit enjoyment. Balance prudence with living well. Age Pension and home equity provide safety nets. Set a reasonable target final balance and enjoy your money.'
        }
      ]
    },
    {
      id: 'fees-taxes',
      title: 'Fees & Taxes',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" /></svg>,
      questions: [
        {
          q: 'What fees are included in the calculations?',
          a: 'We include admin fees ($52 + 0.10%), investment fees (0.5%), and transaction costs (0.08%). You can adjust these to match your super fund\'s actual fees. Industry research found many calculators don\'t match their own Product Disclosure Statement fees - ours are transparent and customizable to your actual costs.'
        },
        {
          q: 'How are taxes calculated?',
          a: 'We include 15% tax on super earnings during accumulation, tax-free withdrawals after age 60, and age pension income/asset tests. We model pension phase correctly (0% tax on earnings vs 15% accumulation). Division 293 tax (additional 15% for high earners) is included in SMSF calculators. All calculations use current ATO rules and rates.'
        },
        {
          q: 'What about Division 293 tax?',
          a: 'Division 293 tax (additional 15% on concessional contributions for high earners over $250k income) is included in our SMSF calculators but not in the basic retirement calculator. If your income exceeds $250k, use the Advanced or SMSF calculators for accurate projections. This additional tax can significantly impact high earners\' accumulation phase.'
        }
      ]
    },
    {
      id: 'smsf',
      title: 'SMSF Features',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
      questions: [
        {
          q: 'What SMSF features are available?',
          a: 'All Access subscribers get access to SMSF calculators including LRBA analysis, Division 293 calculations, contribution strategies, compliance monitoring, and investment optimization tools.'
        },
        {
          q: 'How do I access SMSF calculators?',
          a: 'SMSF features require All Access. Once subscribed, you\'ll see additional calculators in the navigation menu.'
        },
        {
          q: 'Are SMSF calculations compliant with ATO rules?',
          a: 'Yes, our SMSF calculators use current ATO rules and regulations. However, always consult with a qualified SMSF professional for specific advice.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Issues',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      questions: [
        {
          q: 'The calculator isn\'t working properly. What should I do?',
          a: 'Try refreshing the page, clearing your browser cache, or using a different browser. If the issue persists, contact our support team.'
        },
        {
          q: 'Can I use this on mobile devices?',
          a: 'Yes, our calculators are fully responsive and optimized for mobile devices. We recommend using the latest version of Chrome, Safari, or Firefox.'
        },
        {
          q: 'Is my data secure?',
          a: 'Yes, all calculations are performed locally in your browser. We don\'t store your personal financial data on our servers. Only anonymous usage analytics are collected.'
        },
        {
          q: 'Why is the page loading slowly?',
          a: 'Complex calculations may take a few seconds. Ensure you have a stable internet connection and try closing other browser tabs to free up memory.'
        }
      ]
    },
    {
      id: 'subscription',
      title: 'Subscription & Billing',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
      questions: [
        {
          q: 'What\'s included in Free Calculators?',
          a: 'Free Calculators include the Basic Retirement Snapshot, Accumulation Calculator, Age Pension estimates, and two free Advanced runs. Advanced Retirement unlocks the full retirement engine; All Access adds SMSF tools.'
        },
        {
          q: 'How much do the paid plans cost?',
          a: 'Advanced Retirement is $14.99/month or $149/year. All Access is $39.99/month or $399/year and includes the full SMSF suite.'
        },
        {
          q: 'Can I cancel my subscription anytime?',
          a: 'Yes. Use Billing & subscription (footer), Manage billing next to your PRO badge on desktop, or Menu → Support on mobile—it opens Stripe so you can cancel or update payment. You keep access until the end of your billing period. If this device doesn\'t recognise your subscription (e.g. missing Stripe ids in the browser), email support@supercalcpro.com.au from the email you used at checkout.'
        },
        {
          q: 'Do you offer refunds?',
          a: 'We offer a 30-day money-back guarantee. If you\'re not satisfied, contact support within 30 days for a full refund.'
        }
      ]
    }
  ];

  const filteredData = faqData.map(section => ({
    ...section,
    questions: section.questions.filter(q => 
      q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.questions.length > 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Help Center</h1>
          <p className="text-xl text-gray-600 mb-8">
            Find answers to common questions about SuperCalc Pro
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search help articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
          </div>
        </div>

        {/* Contact Options */}
        <div className="grid md:grid-cols-1 gap-6 mb-12 max-w-md mx-auto">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <svg className="w-8 h-8 text-green-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Support</h3>
            <p className="text-gray-600 mb-4">For all inquiries and support requests</p>
            <a href="mailto:support@supercalcpro.com.au" className="text-green-600 hover:text-green-700 font-medium">
              support@supercalcpro.com.au
            </a>
            <p className="text-sm text-gray-500 mt-2">Response time: 24 hours</p>
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6">
          {filteredData.map((section) => (
            <div key={section.id} className="bg-white rounded-lg shadow-sm border">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {section.icon}
                  <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                </div>
                {openSections[section.id] ? (
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                )}
              </button>
              
              {openSections[section.id] && (
                <div className="px-6 pb-4">
                  <div className="space-y-4">
                    {section.questions.map((faq, index) => (
                      <div key={index} className="border-l-4 border-blue-200 pl-4">
                        <h3 className="font-medium text-gray-900 mb-2">{faq.q}</h3>
                        <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* No Results */}
        {searchTerm && filteredData.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600 mb-4">
              Try searching with different keywords or contact our support team.
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear search
            </button>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-12 bg-blue-600 rounded-lg p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Still need help?</h3>
          <p className="text-blue-100 mb-6">
            Our support team is here to help you get the most out of SuperCalc Pro.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@supercalcpro.com.au"
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Contact Support
            </a>
            <a
              href="tel:+61234567890"
              className="border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-blue-600 transition-colors"
            >
              Call Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
